/**
 * @fileoverview Company Information Component
 *
 * This file implements the step in the onboarding process where users provide
 * information about their current or most recent company. It collects data about
 * industry, organization type, structure, size, and other relevant details.
 *
 * Key features:
 * - Dynamic form field rendering based on configuration
 * - Cascading dropdowns (industry → industry sector)
 * - Conditional field display
 * - Form validation
 * - Integration with global state management
 * - Navigation between onboarding steps
 */

import { Button, ButtonVariant, Select, Label, LabelSize, SelectSize, IconName, ButtonIconAlign, TextInput, TextInputWidth, TextInputSize } from '@eightfold.ai/octuple';
import React, { useState, useEffect, useContext } from 'react';
import { actionButtonStyle } from '../../commons/commonStyles';
import { useNavigate } from 'react-router';
import { globalContext } from '../../store';

import { checkSubDropDownValues, updateDemographicData, validateDynamicForm, ShowStepper, resolveError, handleDemographicAcknowledge } from '../../commons/commonFunctions';
import { validateFields, validationOptions } from '../../ValidationSchema';
import ConfirmationModal from '../../commons/ConfirmationModal';

/**
 * Company Component
 *
 * Renders the company information step of the onboarding process where users provide
 * information about their organization, industry, and work environment.
 *
 * @param {Object} props - Component props containing form configuration and pre-filled data
 * @returns {JSX.Element} Company information form
 */
const Company = (props: any) => {
  const navigate = useNavigate();

  const { globalState, dispatch } = useContext(globalContext);
  // State variables for form fields
  const [orgType, setOrgType] = useState<any>('');
  const [orgStructure, setOrgStructure] = useState<any>('');
  const [orgSize, setOrgSize] = useState<any>('');
  const [orgCycle, setOrgCycle] = useState<any>('');
  const [orgRevenue, setOrgRevenue] = useState<any>('');
  const [workingEnvironment, setWorkingEnvironment] = useState<any>('');
  const [errorMsg, setErrorMsg] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [showDemographicModal, setShowDemographicModal] = useState(false);

  // Industry-related state variables
  const [industry, setIndustry] = useState('');
  const [industrySector, setIndustrySector] = useState('');
  const [describeIndustrySec, setDescribeIndustrySec] = useState(false);
  const [industrySecTxt, setindustrySecTxt] = useState('');
  const [errors, setErrors] = useState<any>({});

  // Industry sector dropdown options
  const [industrySectOptions, setIndustrySectOptions] = useState<any>([]);
  const [disableIndustrySec, setDisableIndustrySec] = useState(true);
  const otherTxt = 'Other';
  const otherOptionList = [{ text: `${otherTxt}`, value: `${otherTxt}` }];
  /**
   * Effect hook to initialize form fields with existing data
   *
   * Populates the form fields with data passed through props, typically when
   * a user has previously entered information or is returning to edit
   * their profile information.
   */
  useEffect(() => {
    if (props) {
      setOrgType(props?.orgType);
      setOrgStructure(props?.orgStructure);
      setOrgSize(props?.orgSize);
      setOrgCycle(props?.orgCycle);
      setOrgRevenue(props?.orgRevenue);
      setWorkingEnvironment(props?.workingEnvironment);
      setIndustry(props?.industry);
      
      // Handle "Prefer not to respond" for industry - hide industry sector field
      if (props?.industry === 'Prefer not to respond') {
        setIndustrySector('Prefer not to respond');
        setIndustrySectOptions([]);
        setDisableIndustrySec(true);
      } else if (props.industrySector && props?.industrySector?.includes('~')) {
        setindustrySecTxt(props?.industrySector.replace('OTH~', ''));
        setIndustrySector(otherTxt);
      } else if (props.industrySector) {
        setIndustrySector(props.industrySector);
      }
    }
  }, [props]);

  /**
   * Effect hook to manage industry sector dropdown behavior
   *
   * Handles special cases for industry sector dropdown:
   * - Setting 'Not Applicable' as the default when it's the only option
   * - Setting 'Other' as the default when it's the only option
   * - Clearing the selection when industry changes and the current selection is 'Not Applicable'
   */
  useEffect(() => {
    if (industrySectOptions && industrySectOptions?.[0]?.text === 'Not Applicable') {
      setIndustrySector('Not Applicable');
    } else if (industrySectOptions && industrySectOptions?.[0]?.text === otherTxt) {
      setIndustrySector(otherTxt);
    } else if (industrySectOptions && industrySectOptions?.[0]?.text !== 'Not Applicable' && industry && industrySector === 'Not Applicable') {
      setIndustrySector('');
    }
  }, [industrySectOptions]);
  /**
   * Handles industry dropdown change
   *
   * When the industry selection changes:
   * 1. Updates the industry state
   * 2. Fetches related industry sectors from lookUpData
   * 3. Manages special cases like 'Other' selection
   * 4. Updates the industry sector dropdown options accordingly
   * 5. Hides industry sector field when "Prefer not to respond" is selected
   *
   * @param {Object} value - Selected industry value from dropdown
   */
  const changeIndustry = (value: any) => {
    setIndustry(value?.[0]);
    const options = checkSubDropDownValues(value?.[0], 'Industry Sector', globalState?.lookUpData);
    if (value?.[0] && options && options?.length > 0) {
      if (value?.[0] === otherTxt) {
        setIndustrySectOptions(otherOptionList);
        setDisableIndustrySec(false);
        setDescribeIndustrySec(true);
        if (props.industrySector && props.industrySector?.includes('~')) {
          setindustrySecTxt(props.industrySector.replace('OTH~', ''));
        }
        return;
      }
      setIndustrySectOptions(options);
      setDisableIndustrySec(false);
      setindustrySecTxt('');
    } else if (value?.[0] === 'Prefer not to respond') {
      setIndustrySectOptions([]);
      setIndustrySector('Prefer not to respond');
      setDisableIndustrySec(true);
      setindustrySecTxt('');
      setDescribeIndustrySec(false);
    }
    if (!value?.[0]) {
      setDisableIndustrySec(true);
    }
  };

  /**
   * Handles industry sector dropdown change
   *
   * When the industry sector selection changes:
   * 1. Updates the industry sector state
   * 2. Handles the special case for 'Other' selection by showing a text input field
   * 3. Pre-populates the text field with existing data if available
   *
   * @param {Object} value - Selected industry sector value from dropdown
   */
  const changeIndustrySector = (value: any) => {
    if (industrySector !== 'Not Applicable') {
      setIndustrySector(value?.[0]);
    }

    if (value?.[0] === otherTxt) {
      setDescribeIndustrySec(true);
      setindustrySecTxt(props?.industrySector?.replace('OTH~', ''));
    } else {
      setDescribeIndustrySec(false);
    }
  };

  /**
   * Validates industry sector input when 'Other' is selected
   *
   * Applies text field validation rules to the custom industry sector input
   * when the user selects 'Other' from the industry sector dropdown.
   *
   * @returns {Object} Object containing validation error message if any
   */
  const validationForIndustrySector = () => {
    let industrySecDesMsg;

    if (industrySector === otherTxt) {
      industrySecDesMsg = validateFields.validate({ textField: industrySecTxt || '' }, validationOptions)?.error?.details[0]?.message;
    }

    return { industrySecDesMsg };
  };

  /**
   * Validates all form fields before submission
   *
   * Creates a configuration of all form fields that need validation based on what's
   * displayed in the UI. Applies both standard dropdown validation and custom
   * validation for special fields like the 'Other' industry sector text input.
   *
   * @returns {boolean} True if the form has validation errors, false if valid
   */
  const validateFormData = () => {
    // Define all possible fields that might need validation
    const fieldConfigs = [
      { name: 'showIndustry', value: industry, type: 'dropDown' },
      { name: 'showIndustrySector', value: industrySector, type: 'dropDown' },
      { name: 'showOrgType', value: orgType, type: 'dropDown' },
      { name: 'showOrgStructure', value: orgStructure, type: 'dropDown' },
      { name: 'showOrgSize', value: orgSize, type: 'dropDown' },
      { name: 'showOrgCycle', value: orgCycle, type: 'dropDown' },
      { name: 'showOrgRevenue', value: orgRevenue, type: 'dropDown' },
      { name: 'showWorkingEnvironment', value: workingEnvironment, type: 'dropDown' },
    ];

    // Filter to only include fields that are actually shown on the form
    const filteredFieldConfigs = fieldConfigs?.filter(field => props?.showFields?.[field.name] === true);
    const customValidations = {
      industrySecDesMsg: () => validationForIndustrySector()?.industrySecDesMsg,
    };

    const isFormInvalid = validateDynamicForm(filteredFieldConfigs, customValidations, setErrors);
    return isFormInvalid;
  };

  /**
   * Saves company data and navigates to the next onboarding step
   *
   * This function:
   * 1. Validates the form data
   * 2. Constructs the payload for API submission
   * 3. Updates the global context with the new data
   * 4. Calls the updateDemographicData function to persist the data
   * 5. Navigates to the next onboarding step upon success
   */
  const saveCompanyData = () => {
    // Return early if validation fails
    if (validateFormData()) return;

    // Determine industrySector value based on industry selection
    let industrySectorValue = industrySector;
    if (industry === 'Prefer not to respond') {
      industrySectorValue = 'Prefer not to respond';
    } else if (industrySector === otherTxt) {
      industrySectorValue = `OTH~${industrySecTxt.trim()}`;
    }

    // Prepare API payload
    const payload = {
      demographicSurveyData: {
        orgType: orgType,
        orgStructure: orgStructure,
        orgSize: orgSize,
        orgCycle: orgCycle,
        orgRevenue: orgRevenue,
        workingEnvironment: workingEnvironment,
        industry: industry,
        industrySector: industrySectorValue,
      },
      isProfileCompleted: ((!props?.isPersonalStepVisible) ? true : false), // Mark profile as completed only if all steps are visible
    };

    // Prepare data for global context update
    const updatedDataForContext: any = {
      demographicSurveyData: {
        ...globalState?.leaderData?.demographicSurveyData,
        orgType: orgType,
        orgStructure: orgStructure,
        orgSize: orgSize,
        orgCycle: orgCycle,
        orgRevenue: orgRevenue,
        workingEnvironment: workingEnvironment,
        industry: industry,
        industrySector: industrySectorValue,
      },
      isProfileCompleted: ((!props?.isPersonalStepVisible) ? true : false), // Mark profile as completed only if all steps are visible
    };


    const newContextData = { ...globalState?.leaderData, ...updatedDataForContext };
    const redirectUrl = ((props?.isPersonalStepVisible) ? '/register-personal' : '/dashboard');
    updateDemographicData({ payload, leaderProfileId: props.leaderProfileId, redirectUrl, setErrorMsg, newContextData, dispatch, navigate, setShowLoader });
  };
  /**
   * Navigates back to the previous onboarding step (Role)
   *
   * This function handles the "Back" button click and redirects the user
   * to the previous step in the onboarding flow.
   */
  const backtoRolePage = () => {
    const backUrl = props?.isRoleStepVisible ? '/register-role' :  '/register-account';
    navigate(backUrl);
  };

  /**
   * Effect to show demographic modal on first demographic page
   * Modal appears if this is the first demographic step, demographics are enabled,
   * and the user hasn't acknowledged yet
   */

  const demographicsEnabled = props?.anyDemographicsEnabled === true;
  const isAcknowledged = props?.demographicAcknowledged === true;
  useEffect(() => {
    if (!isAcknowledged && demographicsEnabled) {
      setShowDemographicModal(true);
    }
  }, [isAcknowledged, demographicsEnabled]);

  // Determine if fields should be disabled (when modal hasn't been acknowledged)
  const fieldsDisabled = !isAcknowledged;

  return (
    <div className='accountWrapper'>
      <ShowStepper 
        activeIndex={props?.activeIndex} 
        isRoleStepVisible={props?.isRoleStepVisible}
        isCompanyStepVisible={props?.isCompanyStepVisible}
        isPersonalStepVisible={props?.isPersonalStepVisible}
      />
      <h2 className='onboardingTitle'>About your current or most recent company</h2>

      {/* Only render form if organization type options are available */}
      {props?.orgTypeOptions?.length > 0 && (
        <>
          {/* Industry dropdown field */}
          {props?.showFields?.showIndustry && (
            <div className='fieldWrap'>
              <Label htmlFor='industry' classNames='labelText' size={LabelSize.Medium} text='What industry do you work in?' />
              <Select
                id='industry'
                data-test-id='industry'
                onOptionsChange={(value: any) => {
                  changeIndustry(value);
                  resolveError('showIndustry', errors, setErrors);
                }}
                size={SelectSize.Large}
                classNames='selectField'
                options={props?.industryOptions}
                defaultValue={props?.industry}
                status={errors?.showIndustryStatus}
                disabled={fieldsDisabled}
              />
              {errors?.showIndustryMsg && <span className='errorMessage'>{errors?.showIndustryMsg}</span>}
            </div>
          )}
          {/* Industry sector dropdown field - only shown when industry is selected */}
          {industrySectOptions?.length > 0 && (
            <>
              {' '}
              {props?.showFields?.showIndustrySector && (
                <div className='fieldWrap'>
                  <Label htmlFor='industrySector' classNames='labelText' size={LabelSize.Medium} text='What industry sector do you work in?' />
                  <Select
                    id='industrySector'
                    data-test-id='industrySector'
                    onOptionsChange={(value: any) => {
                      changeIndustrySector(value);
                      resolveError('showIndustrySector', errors, setErrors);
                    }}
                    size={SelectSize.Large}
                    classNames='selectField'
                    options={industrySectOptions !== undefined ? industrySectOptions : []}
                    defaultValue={industrySector ?? 'Select'}
                    status={errors?.showIndustrySectorStatus}
                    disabled={disableIndustrySec || fieldsDisabled}
                  />
                  {errors?.showIndustrySectorMsg && <span className='errorMessage'>{errors?.showIndustrySectorMsg}</span>}
                </div>
              )}
            </>
          )}
          {/* Custom text field shown when "Other" is selected for industry sector */}
          {describeIndustrySec && (
            <div className='fieldWrap'>
              <TextInput
                htmlType='text'
                id='industrySecDescribe'
                clearable
                maxlength={100}
                minlength={0}
                name='industrySecDescribe'
                onChange={e => {
                  setindustrySecTxt(e.target.value);
                  resolveError('industrySecDes', errors, setErrors);
                }}
                value={industrySecTxt}
                defaultValue={industrySecTxt}
                inputWidth={TextInputWidth.fill}
                classNames='inputTextField'
                size={TextInputSize.Large}
                status={errors?.industrySecDesStatus}
                disabled={fieldsDisabled}
              />
              {errors?.industrySecDesMsg && <span className='errorMessage'>{errors?.industrySecDesMsg}</span>}
            </div>
          )}

          {/* Organization type dropdown field */}
          {props?.showFields?.showOrgType && (
            <div className='fieldWrap'>
              <Label htmlFor='orgType' classNames='labelText' size={LabelSize.Medium} text='What type of organization do you work for?' />
              <Select
                id='orgType'
                defaultValue={props?.orgType}
                onOptionsChange={(e: any) => {
                  setOrgType(e?.[0]);
                  resolveError('showOrgType', errors, setErrors);
                }}
                options={props?.orgTypeOptions}
                classNames='selectField'
                size={SelectSize.Large}
                status={errors?.showOrgTypeStatus}
                disabled={fieldsDisabled}
              />
              {errors?.showOrgTypeMsg && <span className='errorMessage'>{errors?.showOrgTypeMsg}</span>}
            </div>
          )}

          {/* Organization structure dropdown field */}
          {props?.showFields?.showOrgStructure && (
            <div className='fieldWrap'>
              <Label htmlFor='orgStructure' classNames='labelText' size={LabelSize.Medium} text='What is the organizational structure of your company?' />
              <Select
                id='orgStructure'
                defaultValue={props?.orgStructure}
                onOptionsChange={(e: any) => {
                  setOrgStructure(e?.[0]);
                  resolveError('showOrgStructure', errors, setErrors);
                }}
                options={props?.orgStructureOptions}
                classNames='selectField'
                size={SelectSize.Large}
                status={errors?.showOrgStructureStatus}
                disabled={fieldsDisabled}
              />
              {errors?.showOrgStructureMsg && <span className='errorMessage'>{errors?.showOrgStructureMsg}</span>}
            </div>
          )}
          {/* Organization size dropdown field */}
          {props?.showFields?.showOrgSize && (
            <div className='fieldWrap'>
              <Label htmlFor='orgSize' classNames='labelText' size={LabelSize.Medium} text='What is the size of your organization?' />
              <Select
                id='orgSize'
                defaultValue={props?.orgSize}
                onOptionsChange={(e: any) => {
                  setOrgSize(e?.[0]);
                  resolveError('showOrgSize', errors, setErrors);
                }}
                options={props?.orgSizeOptions}
                classNames='selectField'
                size={SelectSize.Large}
                status={errors?.showOrgSizeStatus}
                disabled={fieldsDisabled}
              />
              {errors?.showOrgSizeMsg && <span className='errorMessage'>{errors?.showOrgSizeMsg}</span>}
            </div>
          )}

          {/* Organization cycle/stage dropdown field */}
          {props?.showFields?.showOrgCycle && (
            <div className='fieldWrap'>
              <Label htmlFor='orgCycle' classNames='labelText' size={LabelSize.Medium} text='What cycle/stage is your organization currently in?' />
              <Select
                id='orgCycle'
                defaultValue={props?.orgCycle}
                onOptionsChange={(e: any) => {
                  setOrgCycle(e?.[0]);
                  resolveError('showOrgCycle', errors, setErrors);
                }}
                options={props?.orgCycleOptions}
                classNames='selectField'
                size={SelectSize.Large}
                status={errors?.showOrgCycleStatus}
                disabled={fieldsDisabled}
              />
              {errors?.showOrgCycleMsg && <span className='errorMessage'>{errors?.showOrgCycleMsg}</span>}
            </div>
          )}

          {/* Organization revenue dropdown field */}
          {props?.showFields?.showOrgRevenue && (
            <div className='fieldWrap'>
              <Label htmlFor='orgRevenue' classNames='labelText' size={LabelSize.Medium} text='How much revenue does your company generate annually?' />
              <Select
                id='orgRevenue'
                defaultValue={props?.orgRevenue}
                onOptionsChange={(e: any) => {
                  setOrgRevenue(e?.[0]);
                  resolveError('showOrgRevenue', errors, setErrors);
                }}
                options={props?.orgRevenueOptions}
                classNames='selectField'
                size={SelectSize.Large}
                status={errors?.showOrgRevenueStatus}
                disabled={fieldsDisabled}
              />
              {errors?.showOrgRevenueMsg && <span className='errorMessage'>{errors?.showOrgRevenueMsg}</span>}
            </div>
          )}

          {/* Working environment dropdown field */}
          {props?.showFields?.showWorkingEnvironment && (
            <div className='fieldWrap'>
              <Label htmlFor='workingEnvironment' classNames='labelText' size={LabelSize.Medium} text='What type of environment do you work in?' />
              <Select
                id='workingEnvironment'
                defaultValue={props?.workingEnvironment}
                onOptionsChange={(e: any) => {
                  setWorkingEnvironment(e?.[0]);
                  resolveError('showWorkingEnvironment', errors, setErrors);
                }}
                options={props?.workingEnvironmentOptions}
                classNames='selectField'
                size={SelectSize.Large}
                status={errors?.showWorkingEnvironmentStatus}
                disabled={fieldsDisabled}
              />
              {errors?.showWorkingEnvironmentMsg && <span className='errorMessage'>{errors?.showWorkingEnvironmentMsg}</span>}
            </div>
          )}

          {/* Form navigation buttons */}
          <div className='fieldWrap-Btn-multiple'>
            {/* Back button with left chevron icon */}

            <Button
              text='Previous'
              variant={ButtonVariant.Secondary}
              classNames='backButton'
              onClick={() => backtoRolePage()}
              transparent={true}
              iconProps={{ path: IconName.mdiChevronLeft }}
              alignIcon={ButtonIconAlign.Left}
              disabled={fieldsDisabled}
            />
            {/* Continue button with loading state */}
            <Button
              text='Continue'
              variant={ButtonVariant.Secondary}
              classNames='actionButton'
              style={actionButtonStyle}
              onClick={() => {
                saveCompanyData();
              }}
              iconProps={showLoader ? { path: IconName.mdiLoading, spin: true } : {path:IconName.mdiArrowRight}}
              alignIcon={ButtonIconAlign.Right}
              disabled={showLoader || fieldsDisabled}
            />
          </div>
        </>
      )}
      {/* API error message */}
      {errorMsg && <span className='apiErrorMessage'>An error occurred</span>}

      {showDemographicModal && (
        <ConfirmationModal
          header='How we use your responses'
          showModal={showDemographicModal}
          bodyText='The following demographic questions are voluntary and will not be used in the scoring of your assessment or in any employment, promotion, or development decisions. This information is collected solely for research, validation, and fairness monitoring purposes to help ensure the assessment functions consistently and equitably across different groups. Responses will be analyzed in aggregate and handled in accordance with applicable privacy and data protection requirements.'
          showCancelButton={false}
          onContinue={() => handleDemographicAcknowledge(dispatch, setShowDemographicModal)}
          continueBtnTxt={'I understand'}
          onClose={() => setShowDemographicModal(false)}
          isAcknowledge={true}
          maskClosable={false}
        />
      )}
    </div>
  );
};

export default Company;
